import { apiRequest, postJson } from "../auth/api";

export type RelationType = "owner" | "co_owner" | "breeder" | "handler" | "editor" | "custodian";
export type AuthorityScope =
  | "profile:read"
  | "profile:update_safe"
  | "profile:request_sensitive"
  | "ownership:manage"
  | "transfer:initiate"
  | "delegates:manage"
  | "health:manage"
  | "titles:manage"
  | "media:manage";

export interface ClaimItem {
  id: string;
  dogId: string;
  dogName: string;
  dogSlug: string;
  claimantUserId: string;
  claimantDisplayName: string;
  relationType: Exclude<RelationType, "custodian">;
  requestedScopes: AuthorityScope[];
  explanation: string;
  evidenceId: string;
  status: "pending" | "approved" | "rejected" | "cancelled" | "disputed";
  baseDogVersion: number;
  relationshipStartsAt: string;
  relationshipEndsAt: string | null;
  reviewerNote: string | null;
  reviewedAt: string | null;
  createdAt: string;
}

export interface TransferItem {
  id: string;
  dogId: string;
  dogName: string;
  dogSlug: string;
  fromOwnerUserId: string;
  fromOwnerDisplayName: string;
  toUserId: string;
  toDisplayName: string;
  status: "pending_recipient" | "accepted" | "completing" | "completed" | "rejected" | "cancelled" | "expired" | "disputed";
  baseDogVersion: number;
  effectiveAt: string;
  invitationExpiresAt: string;
  note: string | null;
  recipientNote: string | null;
  moderatorNote: string | null;
  version: number;
  createdAt: string;
}

export interface DelegationItem {
  id: string;
  dogId: string;
  dogName: string;
  dogSlug: string;
  grantorUserId: string;
  delegateUserId: string;
  delegateDisplayName: string;
  scopes: AuthorityScope[];
  status: "invited" | "active" | "rejected" | "revoked" | "expired";
  startsAt: string;
  endsAt: string | null;
  invitationExpiresAt: string;
  note: string | null;
  version: number;
  createdAt: string;
}

export interface SensitiveEditItem {
  id: string;
  dogId: string;
  dogName: string;
  dogSlug: string;
  requestedByUserId: string;
  requestedByDisplayName: string;
  baseVersion: number;
  patch: Record<string, unknown>;
  criticalFields: string[];
  sourceSummary: string;
  sourceUrl: string | null;
  evidenceId: string | null;
  beforeSnapshot: Record<string, unknown>;
  afterSnapshot: Record<string, unknown>;
  status: "pending" | "approved" | "rejected" | "cancelled";
  reviewerNote: string | null;
  reviewedAt: string | null;
  createdAt: string;
}

export interface OwnershipDashboard {
  dog: {
    id: string;
    slug: string;
    registeredName: string;
    workflowStatus: string;
    version: number;
  };
  viewer: {
    relationType: RelationType | null;
    scopes: AuthorityScope[];
    canClaim: boolean;
    canTransfer: boolean;
    canDelegate: boolean;
    canRequestSensitiveEdit: boolean;
  };
  assignments: Array<{
    id: string;
    userId: string;
    displayName: string;
    publicSlug: string | null;
    relationType: RelationType;
    scopes: AuthorityScope[];
    startsAt: string;
    endsAt: string | null;
    sourceType: string;
    active: boolean;
  }>;
  claims: ClaimItem[];
  transfers: TransferItem[];
  delegations: DelegationItem[];
  editRequests: SensitiveEditItem[];
  history: Array<{
    id: string;
    eventType: string;
    relationType: string | null;
    subjectUserId: string | null;
    actorUserId: string | null;
    sourceType: string;
    sourceId: string;
    scopes: string[];
    snapshot: Record<string, unknown>;
    createdAt: string;
  }>;
}

export interface OwnershipInbox {
  claims: ClaimItem[];
  transfers: TransferItem[];
  delegations: DelegationItem[];
  editRequests: SensitiveEditItem[];
}

export interface ModerationQueue {
  claims: ClaimItem[];
  transfers: TransferItem[];
  editRequests: SensitiveEditItem[];
}

export function getOwnershipInbox(): Promise<OwnershipInbox> {
  return apiRequest<OwnershipInbox>("/api/v1/ownership/me");
}

export function getOwnershipDashboard(dogId: string): Promise<{ dashboard: OwnershipDashboard }> {
  return apiRequest<{ dashboard: OwnershipDashboard }>(`/api/v1/ownership/dogs/${encodeURIComponent(dogId)}`);
}

export async function uploadEvidence(
  dogId: string,
  purpose: "claim" | "edit_request" | "transfer",
  file: File,
): Promise<{ evidence: { id: string; originalFilename: string; byteSize: number; sha256: string; expiresAt: string } }> {
  const form = new FormData();
  form.set("purpose", purpose);
  form.set("file", file);
  return apiRequest(`/api/v1/ownership/dogs/${encodeURIComponent(dogId)}/evidence`, {
    method: "POST",
    body: form,
  });
}

export function createClaim(
  dogId: string,
  input: {
    relationType: string;
    explanation: string;
    evidenceId: string;
    expectedDogVersion: number;
    relationshipStartsAt: string;
    relationshipEndsAt?: string | null;
  },
): Promise<{ claim: ClaimItem }> {
  return postJson(`/api/v1/ownership/dogs/${encodeURIComponent(dogId)}/claims`, input);
}

export function cancelClaim(claimId: string): Promise<{ claim: ClaimItem }> {
  return postJson(`/api/v1/ownership/claims/${encodeURIComponent(claimId)}/cancel`, {});
}

export function createTransfer(
  dogId: string,
  input: {
    recipientSlug: string;
    effectiveAt: string;
    invitationExpiresAt: string;
    note: string;
    expectedDogVersion: number;
    evidenceId?: string;
  },
): Promise<{ transfer: TransferItem }> {
  return postJson(`/api/v1/ownership/dogs/${encodeURIComponent(dogId)}/transfers`, input);
}

export function respondTransfer(
  transferId: string,
  input: { action: "accept" | "reject"; note?: string; expectedVersion: number },
): Promise<{ transfer: TransferItem }> {
  return postJson(`/api/v1/ownership/transfers/${encodeURIComponent(transferId)}/respond`, input);
}

export function finalizeTransfer(
  transferId: string,
  input: { expectedTransferVersion: number; expectedDogVersion: number; moderatorNote?: string },
): Promise<{ transfer: TransferItem }> {
  return postJson(`/api/v1/ownership/transfers/${encodeURIComponent(transferId)}/finalize`, input);
}

export function cancelTransfer(
  transferId: string,
  input: { expectedVersion: number; note?: string },
): Promise<{ transfer: TransferItem }> {
  return postJson(`/api/v1/ownership/transfers/${encodeURIComponent(transferId)}/cancel`, input);
}

export function createDelegation(
  dogId: string,
  input: {
    delegateSlug: string;
    scopes?: AuthorityScope[];
    startsAt: string;
    endsAt?: string | null;
    invitationExpiresAt: string;
    note?: string;
  },
): Promise<{ delegation: DelegationItem }> {
  return postJson(`/api/v1/ownership/dogs/${encodeURIComponent(dogId)}/delegations`, input);
}

export function respondDelegation(
  delegationId: string,
  input: { action: "accept" | "reject"; expectedVersion: number },
): Promise<{ delegation: DelegationItem }> {
  return postJson(`/api/v1/ownership/delegations/${encodeURIComponent(delegationId)}/respond`, input);
}

export function revokeDelegation(
  delegationId: string,
  input: { expectedVersion: number; note?: string },
): Promise<{ delegation: DelegationItem }> {
  return postJson(`/api/v1/ownership/delegations/${encodeURIComponent(delegationId)}/revoke`, input);
}

export function createSensitiveEdit(
  dogId: string,
  input: {
    patch: Record<string, unknown>;
    sourceSummary: string;
    sourceUrl?: string;
    evidenceId?: string;
    expectedDogVersion: number;
  },
): Promise<{ editRequest: SensitiveEditItem }> {
  return postJson(`/api/v1/ownership/dogs/${encodeURIComponent(dogId)}/edit-requests`, input);
}

export function cancelSensitiveEdit(editRequestId: string): Promise<{ editRequest: SensitiveEditItem }> {
  return postJson(`/api/v1/ownership/edit-requests/${encodeURIComponent(editRequestId)}/cancel`, {});
}

export function getModerationQueue(): Promise<ModerationQueue> {
  return apiRequest<ModerationQueue>("/api/v1/ownership/moderation/queue");
}

export function getClaimCompare(claimId: string): Promise<{ compare: Record<string, unknown> }> {
  return apiRequest(`/api/v1/ownership/moderation/claims/${encodeURIComponent(claimId)}/compare`);
}

export function reviewClaim(
  claimId: string,
  input: { action: "approve" | "reject" | "dispute"; reviewerNote?: string; expectedDogVersion: number },
): Promise<{ claim: ClaimItem }> {
  return postJson(`/api/v1/ownership/moderation/claims/${encodeURIComponent(claimId)}/review`, input);
}

export function getEditCompare(editRequestId: string): Promise<{ compare: Record<string, unknown> }> {
  return apiRequest(`/api/v1/ownership/moderation/edit-requests/${encodeURIComponent(editRequestId)}/compare`);
}

export function reviewSensitiveEdit(
  editRequestId: string,
  input: { action: "approve" | "reject"; reviewerNote?: string; expectedDogVersion: number },
): Promise<{ editRequest: SensitiveEditItem }> {
  return postJson(`/api/v1/ownership/moderation/edit-requests/${encodeURIComponent(editRequestId)}/review`, input);
}

export function getEvidenceAccess(evidenceId: string): Promise<{ url: string; expiresAt: string }> {
  return apiRequest(`/api/v1/ownership/evidence/${encodeURIComponent(evidenceId)}/access`);
}
